"use client";

import { motion } from "motion/react";
import { useMemo } from "react";
import { Button } from "../button";
import {
  FamilyIcon,
  MinistriesIcon,
  OutreachIcon,
  PrayerWorshipIcon,
  ToerustingIcon,
} from "../icons";

export interface SundayServiceCTAProps {
  /** Title text - defaults to Dutch */
  title?: string;
  /** Subtitle text - defaults to Dutch */
  subtitle?: string;
  /** Service time - defaults to "09:30" */
  time?: string;
  /** Day of the week - defaults to "Zondag" */
  day?: string;
  /** CTA button text */
  buttonText?: string;
  /** CTA button href */
  buttonHref?: string;
  /** Additional class name */
  className?: string;
}

// Icon components for floating background
const floatingIcons = [
  { Icon: FamilyIcon, id: "family" },
  { Icon: MinistriesIcon, id: "ministries" },
  { Icon: OutreachIcon, id: "outreach" },
  { Icon: PrayerWorshipIcon, id: "prayer" },
  { Icon: ToerustingIcon, id: "toerusting" },
];

// Generate random positions for icons
function generateIconPositions(count: number) {
  const positions: Array<{
    x: number;
    y: number;
    size: number;
    rotation: number;
    delay: number;
    duration: number;
    floatOffset: number;
  }> = [];

  for (let i = 0; i < count; i++) {
    positions.push({
      x: 5 + Math.random() * 90,
      y: 5 + Math.random() * 90,
      size: 32 + Math.random() * 48,
      rotation: Math.random() * 360,
      delay: Math.random() * 3,
      duration: 18 + Math.random() * 12,
      floatOffset: 8 + Math.random() * 16,
    });
  }

  return positions;
}

export function CtaCard({
  title = "Ontmoet Jezus",
  subtitle = "Elke week komen we samen om God te aanbidden, Zijn Woord te horen en gemeenschap te ervaren",
  time = "09:30 - 11:30",
  day = "Elke Zondag",
  buttonText = "Bezoek Ons",
  buttonHref = "/bezoek-ons",
  className = "",
}: SundayServiceCTAProps) {
  const iconPositions = useMemo(
    () => generateIconPositions(floatingIcons.length * 3),
    [],
  );

  return (
    <section
      className={`mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 md:py-16 lg:px-8 lg:py-20 ${className}`}
    >
      {/* Card Container */}
      <motion.div
        className="relative w-full max-w-6xl mx-auto overflow-hidden rounded-2xl md:rounded-3xl"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, type: "spring", damping: 25 }}
      >
        {/* Card Background with gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-muted via-muted to-boldness" />

        {/* Subtle pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c89657' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Animated glow accent */}
        <motion.div
          className="absolute -top-32 -right-32 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(200, 150, 87, 0.15) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Bottom left accent glow */}
        <motion.div
          className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(235, 55, 0, 0.1) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />

        {/* Floating Icons Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {iconPositions.map((pos, index) => {
            const iconData = floatingIcons[index % floatingIcons.length];
            if (!iconData) return null;
            const { Icon, id } = iconData;

            return (
              <motion.div
                key={`${id}-${index}`}
                className="absolute"
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                }}
                initial={{
                  opacity: 0,
                  rotate: pos.rotation,
                  scale: 0.6,
                }}
                animate={{
                  opacity: [0.04, 0.1, 0.04],
                  y: [0, -pos.floatOffset, 0],
                  rotate: [pos.rotation, pos.rotation + 15, pos.rotation],
                  scale: [0.6, 0.8, 0.6],
                }}
                transition={{
                  duration: pos.duration,
                  delay: pos.delay,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Icon size={pos.size} className="text-strategy-gold" />
              </motion.div>
            );
          })}
        </div>

        {/* Border glow effect */}
        <div className="absolute inset-0 rounded-2xl md:rounded-3xl border border-strategy-gold/20 pointer-events-none" />

        {/* Main Content */}
        <div className="relative z-10 px-6 py-12 md:px-12 md:py-16 lg:px-20 lg:py-20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10 lg:gap-16">
            {/* Left Content */}
            <div className="flex-1 text-center lg:text-left">
              {/* Day & Time Badge */}
              <motion.div
                className="inline-flex items-center gap-3 mb-6"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: 0.1,
                  type: "spring",
                  damping: 25,
                }}
              >
                <span className="px-3 py-1.5 text-xs font-medium tracking-wider uppercase bg-strategy-gold/10 text-strategy-gold border border-strategy-gold/30 rounded-full">
                  {day}
                </span>
                <span className="font-heading text-2xl md:text-3xl font-bold text-strategy-gold tracking-tight">
                  {time}
                </span>
              </motion.div>

              {/* Main Title */}
              <motion.h2
                className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-freedom mb-4 tracking-tight leading-none"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: 0.2,
                  type: "spring",
                  damping: 25,
                }}
              >
                {title.split(" ").map((word, i) => (
                  <span key={i} className={i === 1 ? "text-strategy-gold" : ""}>
                    {word}{" "}
                  </span>
                ))}
              </motion.h2>

              {/* Subtitle */}
              <motion.p
                className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: 0.3,
                  type: "spring",
                  damping: 25,
                }}
              >
                {subtitle}
              </motion.p>

              {/* Ministry Icons Row - Mobile/Tablet */}
              <motion.div
                className="mt-8 flex items-center justify-center lg:justify-start gap-4"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                {floatingIcons.map(({ Icon, id }, index) => (
                  <motion.div
                    key={id}
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.4,
                      delay: 0.6 + index * 0.08,
                      type: "spring",
                      damping: 15,
                    }}
                    whileHover={{
                      scale: 1.15,
                      transition: { duration: 0.2 },
                    }}
                    className="p-2 rounded-lg bg-boldness/50 border border-border/50 cursor-pointer group"
                  >
                    <Icon
                      size={22}
                      className="text-muted-foreground group-hover:text-strategy-gold transition-colors duration-200"
                    />
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Right Content - CTA */}
            <motion.div
              className="flex flex-col items-center lg:items-end gap-4"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.6,
                delay: 0.4,
                type: "spring",
                damping: 25,
              }}
            >
              {/* Large Time Display - Desktop */}
              <div className="hidden lg:block mb-2">
                <motion.div
                  className="font-heading text-7xl xl:text-8xl font-bold text-freedom/40 tracking-tighter leading-none select-none"
                  animate={{
                    opacity: [0.1, 0.15, 0.1],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  {time}
                </motion.div>
              </div>

              {/* CTA Button */}
              <Button
                href={buttonHref}
                colorScheme="strategy-gold"
                variant="primary"
                size="large"
              >
                {buttonText}
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Bottom decorative line */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-strategy-gold/40 to-transparent"
          initial={{ scaleX: 0, opacity: 0 }}
          whileInView={{ scaleX: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.6 }}
        />
      </motion.div>
    </section>
  );
}
